'use client';

import React, { useEffect } from 'react';
import { ToolId } from './utils/slugs';

interface BlogInteractionHandlerProps {
    setActiveTool: (toolId: ToolId) => void;
}

/**
 * Handles all client-side interactions for the blog post page.
 * This allows the main BlogPostPage to remain a Server Component for SEO.
 */
export const BlogInteractionHandler: React.FC<BlogInteractionHandlerProps> = ({ setActiveTool }) => {
    useEffect(() => {
        const contentDiv = document.getElementById('blog-content');
        const tocDiv = document.getElementById('toc-container');

        if (!contentDiv) return;

        const handleToolClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.tagName === 'BUTTON' && target.dataset.toolid) {
                e.preventDefault();
                setActiveTool(target.dataset.toolid as ToolId);
            }
        };

        const handleTocClick = (e: MouseEvent) => {
            const target = e.target as HTMLAnchorElement;
            if (target.tagName === 'A' && target.hash) {
                e.preventDefault();
                const id = target.hash.substring(1);
                const element = document.getElementById(id);
                if (element) {
                    const headerOffset = 100;
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
            }
        };

        contentDiv.addEventListener('click', handleToolClick);
        tocDiv?.addEventListener('click', handleTocClick);

        return () => {
            contentDiv.removeEventListener('click', handleToolClick);
            tocDiv?.removeEventListener('click', handleTocClick);
        };
    }, [setActiveTool]);

    return null; // This component doesn't render anything, it just attaches listeners
};
